import { NewStockClassDraft } from '@commenda/api';
import {
  Button,
  FieldWrapper,
  Input,
  Label,
  NumberInput,
  Sheet,
  SheetContent,
  SheetTitle,
  SingleCombobox,
} from '@commenda/kakashi';
import { StockClassType } from '@prisma/client';
import { useState } from 'react';
import { OwnershipNouns } from '../ownershipNouns';
import { SUBMIT_VALIDATION_TOAST } from './submitToast';

const STOCK_CLASS_TYPE_OPTIONS = [
  { label: 'Common', value: StockClassType.COMMON },
  { label: 'Preferred', value: StockClassType.PREFERRED },
];

interface AddShareClassBottomsheetProps {
  isOpen: boolean;
  onClose: () => void;
  // Staging-only — the parent form holds the draft and creates the
  // StockClass atomically with the Stakeholding write on submit. Cancelling
  // the outer form leaves no orphan class in the corp's pool.
  onStaged: (draft: NewStockClassDraft) => void;
  onError?: (message: string) => void;
  classNouns: OwnershipNouns;
  totalLabel: string;
}

export const AddShareClassBottomsheet = ({
  isOpen,
  onClose,
  onStaged,
  onError,
  classNouns,
  totalLabel,
}: AddShareClassBottomsheetProps) => {
  const [name, setName] = useState('');
  const [type, setType] = useState<StockClassType>(StockClassType.COMMON);
  const [totalShares, setTotalShares] = useState<number | undefined>(undefined);
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [sharesError, setSharesError] = useState<string | undefined>(undefined);

  const reset = () => {
    setName('');
    setType(StockClassType.COMMON);
    setTotalShares(undefined);
    setNameError(undefined);
    setSharesError(undefined);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Nested form (Radix portals don't break React event bubbling).
    // Without stopPropagation the outer shareholder form also submits.
    e.stopPropagation();

    const nextNameError = !name.trim() ? 'Name is required.' : undefined;
    const nextSharesError =
      totalShares == null || !Number.isFinite(totalShares) || totalShares < 1
        ? `${totalLabel} must be at least 1.`
        : undefined;
    setNameError(nextNameError);
    setSharesError(nextSharesError);
    if (nextNameError || nextSharesError) {
      onError?.(SUBMIT_VALIDATION_TOAST);
      return;
    }

    onStaged({
      name: name.trim(),
      type,
      authorizedShares: totalShares as number,
    });
    handleClose();
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <SheetContent side="bottom" hideCloseIcon>
        {/* Scroll the body, not the panel, so the drag handle stays pinned/reachable. */}
        <div className="flex-1 min-h-0 w-full overflow-y-auto overscroll-contain flex flex-col items-center px-10 pt-5 pb-12">
          <form
            onSubmit={handleSubmit}
            className="w-full max-w-xl flex flex-col"
          >
            <div className="w-full flex flex-col gap-8">
              <SheetTitle className="w-full text-2xl leading-8">
                Add {classNouns.lowerSingular}
              </SheetTitle>

              <div className="w-full bg-bg-primary border border-border-secondary rounded-lg">
                <div className="flex flex-col gap-xl p-6">
                  <FieldWrapper>
                    <Label htmlFor="shareClassName" required>
                      Name
                    </Label>
                    <Input
                      id="shareClassName"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        if (nameError) setNameError(undefined);
                      }}
                      placeholder="e.g., Class A"
                      error={nameError}
                    />
                  </FieldWrapper>

                  <FieldWrapper>
                    <Label htmlFor="shareClassType" required>
                      Type
                    </Label>
                    <SingleCombobox
                      id="shareClassType"
                      options={STOCK_CLASS_TYPE_OPTIONS}
                      value={type}
                      setValue={(v) => setType(v as StockClassType)}
                      placeholder="Select a type"
                      hideSearch
                      canClearSelection={false}
                    />
                  </FieldWrapper>

                  <FieldWrapper>
                    <Label htmlFor="shareClassTotalShares" required>
                      {totalLabel}
                    </Label>
                    <NumberInput
                      id="shareClassTotalShares"
                      value={totalShares}
                      onChange={(n) => {
                        setTotalShares(n ?? undefined);
                        if (sharesError) setSharesError(undefined);
                      }}
                      placeholder="10,000,000"
                      min={1}
                      step={1}
                      formatWithCommas
                      error={sharesError}
                    />
                  </FieldWrapper>
                </div>
              </div>
            </div>

            <div className="w-full flex items-center justify-end gap-4 py-4">
              <Button
                variant="secondary"
                size="md"
                type="button"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button variant="primary" size="md" type="submit">
                <span>Add {classNouns.lowerSingular}</span>
              </Button>
            </div>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
};
